import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { CheckCircle, X, AlertCircle } from "lucide-react";
import { cn } from "~/lib/utils";

export type ToastType = "success" | "error" | "default";

export interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  clearToast: () => void;
}

const ToastContext = createContext<ToastContextType>({
  showToast: () => {},
  showSuccess: () => {},
  showError: () => {},
  clearToast: () => {},
});

interface ToastProviderProps extends PropsWithChildren {
  autoHideDuration?: number;
}

export const ToastProvider = ({ children, autoHideDuration = 3500 }: ToastProviderProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [type, setType] = useState<ToastType>("success");

  const clearToast = () => {
    setMessage("");
    setOpen(false);
  };

  const showToast = (message: string, toastType: ToastType = "success") => {
    setMessage(message);
    setType(toastType);
    setOpen(true);
  };

  const showSuccess = (message: string) => {
    showToast(message, "success");
  };

  const showError = (message: string) => {
    showToast(message, "error");
  };

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Auto-hide after specified duration
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setOpen(false);
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [open, autoHideDuration]);

  const getToastStyles = () => {
    switch (type) {
      case "success":
        return {
          container: "bg-white border-green-200",
          icon: "text-green-600",
          text: "text-green-900",
          closeButton: "hover:bg-green-50 text-green-600",
        };
      case "error":
        return {
          container: "bg-white border-red-200",
          icon: "text-red-600",
          text: "text-red-900",
          closeButton: "hover:bg-red-50 text-red-600",
        };
      default:
        return {
          container: "bg-white border-gray-200",
          icon: "text-gray-600",
          text: "text-gray-900",
          closeButton: "hover:bg-gray-50 text-gray-600",
        };
    }
  };

  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle className="h-5 w-5 flex-shrink-0" />;
      case "error":
        return <AlertCircle className="h-5 w-5 flex-shrink-0" />;
      default:
        return <CheckCircle className="h-5 w-5 flex-shrink-0" />;
    }
  };

  const styles = getToastStyles();

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, clearToast }}>
      {children}
      {open && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] animate-in slide-in-from-top-2 duration-300">
          <div
            className={cn(
              "border rounded-lg shadow-lg p-4 min-w-[300px] max-w-[500px]",
              styles.container
            )}
          >
            <div className="flex items-center gap-3">
              <div className={styles.icon}>{getIcon()}</div>
              <div className="flex-1">
                <p className={cn("text-sm font-medium", styles.text)}>{message}</p>
              </div>
              <button
                onClick={handleClose}
                className={cn(
                  "cursor-pointer shrink-0 p-1 rounded-md transition-colors",
                  styles.closeButton
                )}
                aria-label="Close notification"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
};

const useToast = () => {
  return useContext(ToastContext);
};

export default useToast;
